import {parsePaintedWall,wallLength,type PaintedWallGeometry} from './painted-wall-geometry';
import {paintedWallSection} from './painted-wall-sections';

/** Adjacent sections of the same phase share one ribbon; breached intervals produce no volume.
 * Group by path interval, not physical proximity: a retraced intact span can still occupy a gap.
 */
export function paintedWallPhaseVolumes(wall:PaintedWallGeometry,state:PaintedWallSectionState|null):{solid:PaintedWallGeometry[];residue:PaintedWallGeometry[];frozen:PaintedWallGeometry[]} {
  if(!state)return {solid:[wall],residue:[],frozen:[]};
  const out:{solid:PaintedWallGeometry[];residue:PaintedWallGeometry[];frozen:PaintedWallGeometry[]}={solid:[],residue:[],frozen:[]};
  const phases=new Map(state.entries.map(e=>[e.index,e.phase]));
  const count=Math.max(1,Math.ceil((wallLength(wall.points)-1e-6)/state.sectionFt));
  let previous='';
  for(let index=0;index<count;index++) {
    const phase=phases.get(index)??'solid';
    if(phase==='breached'){previous=phase;continue;}
    const part=paintedWallSection(wall,index,state.sectionFt);
    if(!part)throw new Error('Invalid canonical wall section');
    const list=out[phase];
    if(previous===phase)list[list.length-1]!.points.push(...part.geometry.points.slice(1));
    else list.push(part.geometry);
    previous=phase;
  }
  return out;
}

export interface PaintedWallSectionState {
  schema:'ft.wall-sections.v1';
  sectionFt:number;
  revision:number;
  entries:Array<{index:number;inventoryId:string;phase:'solid'|'residue'|'breached'|'frozen'}>;
}

export function parsePaintedWallSectionSnapshot(value:unknown):{geometry:PaintedWallGeometry;sections:PaintedWallSectionState|null}|null {
  if(typeof value==='string') {
    if(value.length>150000)return null;
    try {value=JSON.parse(value);}catch{return null;}
  }
  if(!value||typeof value!=='object')return null;
  const geometry=parsePaintedWall(value);if(!geometry)return null;
  const raw=value as Record<string,unknown>;
  if(!Object.prototype.hasOwnProperty.call(raw,'sections'))return {geometry,sections:null};
  const sections=parsePaintedWallSectionState(geometry,raw.sections);
  return sections?{geometry,sections}:null;
}

/** Read persisted anchor state. Absence is distinct from malformed state; callers decide
 * whether a spell supports sections. Placement wires never supply these generated inventory bindings.
 */
export function parsePaintedWallSectionState(wall:PaintedWallGeometry,value:unknown):PaintedWallSectionState|null {
  if(!value||typeof value!=='object')return null;
  const raw=value as Record<string,unknown>;
  if(raw.schema!=='ft.wall-sections.v1'||typeof raw.sectionFt!=='number'||!Number.isFinite(raw.sectionFt)
    ||raw.sectionFt<=1e-6||typeof raw.revision!=='number'||!Number.isSafeInteger(raw.revision)||raw.revision<0
    ||!Array.isArray(raw.entries))return null;
  const count=Math.max(1,Math.ceil((wallLength(wall.points)-1e-6)/raw.sectionFt));
  if(!Number.isSafeInteger(count)||count>4096||raw.entries.length>count)return null;
  const indices=new Set<number>(),ids=new Set<string>(),entries:PaintedWallSectionState['entries']=[];
  for(const value of raw.entries) {
    if(!value||typeof value!=='object')return null;
    const {index,inventoryId,phase}=value as Record<string,unknown>;
    if(typeof index!=='number'||!Number.isSafeInteger(index)||index<0||index>=count||indices.has(index)
      ||typeof inventoryId!=='string'||inventoryId.length!==36||!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(inventoryId)
      ||(phase!=='solid'&&phase!=='residue'&&phase!=='breached'&&phase!=='frozen'))return null;
    const id=inventoryId.toUpperCase();if(ids.has(id))return null;
    indices.add(index);ids.add(id);entries.push({index,inventoryId:id,phase});
  }
  entries.sort((a,b)=>a.index-b.index);
  return {schema:'ft.wall-sections.v1',sectionFt:raw.sectionFt,revision:raw.revision,entries};
}
